<template>
  <v-container>
    <modal
      name="theme-preview"
      class="envivo-modal"
      @before-open="$h.toggleHtmlScroll"
      @before-close="$h.toggleHtmlScroll"
      transition="pop-out"
      height="90%"
      adaptive
      width="90%"
    >
      <div class="modal-wrapper h-full">
        <div class="absolute right-4 top-4 cursor-pointer" @click="$modal.hide('theme-preview')">
          <v2-icon :data="require('@icon/Icon=x.svg')" width="22px" height="22px"></v2-icon>
        </div>
        <div class="modal-content overflow-y-scroll h-full" v-if="currentThemePreview">
          <div>
            {{ $t("titles.templatePreview", { name: currentThemePreview.name }) }}
          </div>
          <div :class="[isSmView ? '' : 'p-8']">
            <v2-row noSpace class="[isSmView ? '' : '']">
              <v2-col noSpace :class="[isLgView ? 'order-2 col-12 px-0' : 'col-7 pr-16']">
                <div v-if="currentThemePreview.screenshot">
                  <img :src="currentThemePreview.screenshot" alt="" class="w-full" />
                </div>
                <div v-if="!currentThemePreview.screenshot">
                  <img :src="templateThumbnailBig" alt="" class="w-full" />
                </div>
              </v2-col>
              <v2-col :class="[isLgView ? 'order-1 col-12 px-0' : 'col-5']">
                <div class="border-2 border-v2-grey-light p-4 rounded-lg">
                  <div class="mb-2">
                    <div class="text-v2-grey-light">Name:</div>
                    <div>
                      {{ $h.getLanguageValue(currentThemePreview.display_name) }}
                    </div>
                  </div>
                  <div class="mb-2">
                    <div class="text-v2-grey-light">Beschreibung</div>
                    <div>
                      {{ $h.getLanguageValue(currentThemePreview.description) }}
                    </div>
                  </div>
                  <div class="mb-2">
                    <div class="text-v2-grey-light">Erstellt am:</div>
                    <div>
                      {{ $h.formatDate(currentThemePreview.created_at) }}
                    </div>
                  </div>
                </div>
                <div class="mt-8" :class="{ flex: isDesktopView }">
                  <v2-button
                    secondary
                    :fullWidth="!isDesktopView"
                    class="mr-4 flex-1"
                    :class="{ 'mb-2': !isDesktopView }"
                    v-if="currentThemePreview && currentThemePreview.preview_url"
                    @click.native="window.open(currentThemePreview.preview_url, '_blank').focus()"
                  >
                    {{ $t("actions.showLivePreview") }}
                  </v2-button>
                  <v2-button
                    primary
                    :fullWidth="!isDesktopView"
                    class="flex-1"
                    @click.native="selectThemeAndCloseModal(currentThemePreview)"
                    >{{ $t("actions.selectTheme") }}</v2-button
                  >
                </div>
              </v2-col>
            </v2-row>
          </div>
        </div>
      </div>
    </modal>
    <v-row>
      <v-col stretch>
        <v-breadcrumb
          v-if="$route.name === 'createTemplate'"
          :items="breadcrumbCreateTemplateItems"
          :mobile="breadcrumbMobileCreateItem"
        ></v-breadcrumb>
        <v-breadcrumb
          v-if="$route.name === 'editTemplate'"
          :items="breadcrumbEditTemplateItems"
          :mobile="breadcrumbMobileEditItem"
        ></v-breadcrumb>
      </v-col>
    </v-row>
    <v-row>
      <v-col stretch>
        <v-title-bar v-if="$route.name === 'createTemplate'" :title="$t('titles.createTemplate')">
          <v-button class="wrap-btn" primary @click.native="submitTemplate(false)">{{
            $t("actions.createTemplate")
          }}</v-button>
        </v-title-bar>
        <v-title-bar v-if="$route.name === 'editTemplate'" :title="$t('titles.editTemplate')">
          <v-button class="wrap-btn" primary @click.native="submitTemplate(true)">{{ $t("actions.save") }}</v-button>
        </v-title-bar>
      </v-col>
    </v-row>
    <form @submit.prevent>
      <v-row>
        <v-col class="col-12">
          <label :class="{ 'mt-12': $route.name === 'createTemplate' }">{{
            "1. " + $t("template.form.chooseName.label")
          }}</label>
          <p>{{ $t("template.form.chooseName.note") }}</p>
          <v-textfield
            :placeholder="$t('template.name')"
            v-model="name"
            :required="true"
            v-validate="'required'"
            name="name"
            :error="errors.first('name')"
            :data-vv-as="$t('template.name')"
          />
          <label class="mt-12">{{ "2. " + $t("template.form.chooseLang.label") }}</label>
          <p>{{ $t("template.form.chooseLang.note") }}</p>
          <v2-select
            withRequiredLabel
            :placeholder="$t('template.locale')"
            v-model="locale"
            :options="localeOptions"
            optionLabel="label"
            id="locale"
            name="locale"
            v-validate="'required'"
            :error="errors.first('locale')"
            :showLabels="false"
            :data-vv-as="$t('template.locale')"
            @select="
              (locale) => {
                preselectAvailableLanguage(locale);
              }
            "
          ></v2-select>
          <label class="mt-12">{{ "3. " + $t("template.form.chooseAvailableLangs.label") }}</label>
          <p>{{ $t("template.form.chooseAvailableLangs.note") }}</p>
          <v2-select
            withRequiredLabel
            :placeholder="$t('template.languages')"
            v-model="languages"
            :options="languageOptions"
            v-validate="'required'"
            :error="errors.first('languages')"
            optionLabel="label"
            trackBy="label"
            id="languages"
            name="languages"
            :multiple="true"
            :taggable="false"
            :hideSelected="true"
            :closeOnSelect="false"
            :clearOnSelect="false"
            :data-vv-as="$t('template.languages')"
          ></v2-select>
          <label class="mt-12" v-if="$route.name === 'createTemplate'">{{
            "4. " + $t("template.form.chooseTheme.label")
          }}</label>
          <p v-if="$route.name === 'createTemplate'">
            {{ $t("template.form.chooseTheme.note") }}
          </p>
          <v-row mb0 class="-ml-4 mt-8" v-if="$route.name === 'createTemplate'">
            <v-col v-for="theme in combinedThemes" :key="theme.uid">
              <v-card template withOverlay :hidden="selectedTheme !== null && selectedTheme.uid !== theme.uid">
                <div slot="overlay" class="flex flex-col justify-center items-center h-full">
                  <v-button secondary small fixedWidth class="mb-4" @click.native="selectTheme(theme)">{{
                    $t("actions.select")
                  }}</v-button>
                  <v-button
                    secondary
                    small
                    fixedWidth
                    @click.native="showThemeModal(theme)"
                    v-if="
                      theme.preview_url || theme.screenshot || (theme.description && theme.description.default !== '')
                    "
                    >{{ $t("actions.preview") }}</v-button
                  >
                </div>
                <div slot="header">
                  <div>{{ $h.getLanguageValue(theme.display_name) }}</div>
                  <div slot="icon" v-if="selectedTheme !== null && selectedTheme.uid === theme.uid">
                    <v2-icon :data="require('@icon/Icon=check.svg')" width="25" height="25" color="green"></v2-icon>
                  </div>
                </div>
                <div slot="content">
                  <img v-if="theme.thumbnail" :src="theme.thumbnail" alt="" />
                  <img v-if="!theme.thumbnail" :src="templateThumbnailSmall" alt="" />
                </div>
              </v-card>
            </v-col>
          </v-row>
          <div class="form-error" v-if="$route.name === 'createTemplate'">
            {{ errors.first("theme") }}
          </div>
        </v-col>
        <v-col class="col-12">
          <div class="w-full flex justify-end">
            <v-button v-if="$route.name === 'createTemplate'" primary @click.native="submitTemplate(false)">{{
              $t("actions.createTemplate")
            }}</v-button>
            <v-button v-if="$route.name === 'editTemplate'" primary @click.native="submitTemplate(true)">{{
              $t("actions.save")
            }}</v-button>
          </div>
        </v-col>
      </v-row>
    </form>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import globalMixin from "@/mixins/global.js";

export default {
  name: "FormTemplate",
  mixins: [globalMixin],
  data() {
    return {
      templateThumbnailSmall: require("@/assets/svg/template-thumbnail-small.svg"),
      templateThumbnailBig: require("@/assets/svg/template-thumbnail-big.svg"),
      submitActive: false,
      name: "",
      locale: "",
      localeOptions: [],
      combinedThemes: [],
      languages: [],
      languageOptions: [],
      breadcrumbCreateTemplateItems: [
        {
          label: this.$t("menus.settings"),
          route: { name: "Settings", params: { tab: "account" } },
        },
        {
          label: this.$t("menus.templates"),
          route: { name: "Settings", params: { tab: "templates" } },
        },
        {
          label: this.$t("menus.createTemplate"),
          active: true,
        },
      ],
      breadcrumbEditTemplateItems: [
        {
          label: this.$t("menus.settings"),
          route: { name: "Settings", params: { tab: "account" } },
        },
        {
          label: this.$t("menus.templates"),
          route: { name: "Settings", params: { tab: "templates" } },
        },
        {
          label: "",
          route: { name: "Template", params: { uid: this.$route.params.uid } },
        },
        {
          label: this.$t("menus.editTemplate"),
          active: true,
        },
      ],
      breadcrumbMobileCreateItem: {
        label: this.$t("menus.templates"),
        route: { name: "Settings", params: { tab: "templates" } },
      },
      breadcrumbMobileEditItem: {
        label: "",
        route: { name: "Template", params: { uid: "" } },
      },
      selectedTheme: null,
      currentThemePreview: null,
      globalThemeOptions: [],
      themeOptions: [],
    };
  },
  created() {
    this.loadPageData();
  },
  methods: {
    ...mapActions([
      "getTeamLanguages",
      "getTeamThemes",
      "getTemplate",
      "createTemplate",
      "editTemplate",
      "getTeamGlobalThemes",
      "getTeamTemplates",
    ]),
    loadPageData() {
      const vm = this;
      const p1 = vm.getTeamThemes({ params: { per_page: "50" } });
      const p2 = vm.getTeamLanguages();
      const p3 = vm.getTeamGlobalThemes({ params: { per_page: "50" } });
      Promise.all([p1, p2, p3])
        .then(() => {
          if (vm.themes) {
            vm.themes.forEach((theme) => {
              theme.is_global = false;
              vm.themeOptions.push(theme);
            });
          }
          if (vm.globalThemes) {
            vm.globalThemes.forEach((globalTheme) => {
              globalTheme.is_global = true;
              vm.globalThemeOptions.push(globalTheme);
            });
          }

          vm.combinedThemes = vm.globalThemeOptions.concat(vm.themeOptions);
          if (vm.teamLanguages) {
            vm.teamLanguages.forEach((lang) => {
              vm.localeOptions.push({
                label: vm.$t('lang["' + lang + '"]'),
                value: lang,
              });
              vm.languageOptions.push({
                label: vm.$t('lang["' + lang + '"]'),
                value: lang,
              });
            });
          }
          if (vm.$route.name === "editTemplate") this.setTemplate();
          vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
        })
        .catch(() => {
          vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
        });
    },
    setTemplate() {
      this.getTemplate({ template: this.$route.params.uid }).then(() => {
        this.breadcrumbEditTemplateItems[2].label = this.currentTemplate.name;
        this.breadcrumbEditTemplateItems[2].route = {
          name: "Template",
          params: { uid: this.currentTemplate.uid },
        };
        this.breadcrumbMobileEditItem.label = this.currentTemplate.name;
        this.breadcrumbMobileEditItem.route = {
          name: "Template",
          params: { uid: this.currentTemplate.uid },
        };
        this.name = this.currentTemplate.name;
        this.theme = {
          label: this.currentTemplate.theme.name,
          value: this.currentTemplate.theme.uid,
        };
        this.locale = {
          label: this.$t('lang["' + this.currentTemplate.locale + '"]'),
          value: this.currentTemplate.locale,
        };
        this.currentTemplate.languages.forEach((lang) => {
          this.languages.push({
            label: this.$t('lang["' + lang + '"]'),
            value: lang,
          });
        });
      });
    },
    submitTemplate(isEdit) {
      this.errors.clear();
      this.$validator.validateAll().then((result) => {
        if (!isEdit && this.selectedTheme === null) {
          this.errors.add({
            field: "theme",
            msg: this.$t("template.form.validation.theme"),
          });
        }
        let localeMissing = true;
        if (this.languages.some((lang) => lang.value === this.locale.value)) {
          localeMissing = false;
        } else {
          this.errors.add({
            field: "languages",
            msg: this.$t("template.form.validation.localeMissingInAvailableLanguages"),
          });
        }
        if (result && !localeMissing && (this.selectedTheme !== null || isEdit)) {
          const langs = [];
          this.languages.forEach((lang) => {
            langs.push(lang.value);
          });
          const data = {
            name: this.name,
            locale: this.locale.value,
            languages: langs,
          };
          if (this.selectedTheme) {
            if (this.selectedTheme.is_global) {
              data.global_theme_uid = this.selectedTheme.uid;
            } else {
              data.theme_uid = this.selectedTheme.uid;
            }
          }
          if (isEdit) {
            this.editTemplate({
              redirect: true,
              params: { template: this.$route.params.uid },
              data,
            })
              .then((res) => {
                this.$snotify.success(this.$t("toasts.editedTemplate.success"), this.$t("toasts.success"), {
                  icon: this.$successIcon,
                });
                this.$goTo("Template", { uid: res.uid });
              })
              .catch((err) => {
                if (err.response && !err.response.data.error.custom) {
                  this.$snotify.error(this.$t("toasts.editedTemplate.error"), this.$t("toasts.error"), {
                    timeout: 0,
                    icon: this.$errorIcon,
                  });
                }
              });
          } else {
            this.createTemplate({ redirect: true, data })
              .then((res) => {
                this.$snotify.success(this.$t("toasts.createdTemplate.success"), this.$t("toasts.success"), {
                  icon: this.$successIcon,
                });
                this.$goTo("Template", { uid: res.uid, tab: "_general" });
              })
              .catch((err) => {
                if (err.response && !err.response.data.error.custom) {
                  this.$snotify.error(this.$t("toasts.createdTemplate.error"), this.$t("toasts.error"), {
                    timeout: 0,
                    icon: this.$errorIcon,
                  });
                }
              });
          }
        } else {
          this.$snotify.error(this.$t("toasts.createdTemplate.validation"), this.$t("toasts.error"), {
            timeout: 0,
            icon: this.$errorIcon,
          });
        }
      });
    },
    selectTheme(theme) {
      this.selectedTheme = theme;
    },
    showThemeModal(theme) {
      this.currentThemePreview = theme;
      this.$modal.show("theme-preview");
    },
    selectThemeAndCloseModal(theme) {
      this.selectedTheme = theme;
      this.currentThemePreview = null;
      this.$modal.hide("theme-preview");
    },
    preselectAvailableLanguage(locale) {
      if (this.languages.filter((e) => e.value === locale.value).length <= 0) {
        this.languages.push(locale);
      }
    },
  },
  computed: {
    ...mapGetters({
      teams: "auth_get_teams",
      themes: "teams_get_themes",
      globalThemes: "teams_get_global_themes",
      teamLanguages: "teams_get_languages",
      currentTemplate: "templates_get_current_template",
    }),
  },
};
</script>
